import React, { ChangeEvent, FormEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  DateRangePicker,
  ExcelIcon,
  Form,
  Input,
  Pagination,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import { ProductDetailsModal, ProductMenuCodeProvider } from '~/components/product';

import { useDownloadReady, useModal, useToggle } from '~/hooks/common';
import {
  productKeys,
  useGetCategoriesCodesParentCodeQuery,
  useGetCategoriesTypesTypeQuery,
  useGetCodesMenuMenuCodeQuery,
  useGetOrderTypeInfoQuery,
  useGetSkusQuery,
  usePostThirdPartySkuExcelDownloadMutation,
  usePutThirdPartiesSkusMutation,
} from '~/hooks/product';
import { useProductStore, useUserInfoStore } from '~/store';
import {
  focusFirstErrorField,
  formatDate,
  getDateRangeInfo,
  getProductOperatingColumnDefs,
  renderFilterErrorMessage,
  transformToProductSelectOptions as transformToSelectOptions,
  validateProductFields,
} from '~/utils';

import { BREAD_CRUMB, DEFAULT_INPUT_STATUS, OPTION_ALL, PATH, TOAST_MESSAGE } from '~/constants';
import {
  INITIAL_PRODUCT_PAGE_OFFSET,
  INITIAL_PRODUCT_PAGE_SIZE,
  PRODUCT_PAGE_SIZE,
  PRODUCT_USAGE_STATUS,
  TypeProductPageSize,
} from '~/constants/product';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';
import type {
  TypeGetSkusParams,
  TypePostThirdPartySkuExcelDownloadParams,
  TypeProduct,
  TypeProductModalInput,
  TypeProductOperatingFilter,
  TypeProductOperatingPageCodesMenu,
} from '~/types/product.types';

import styles from './ProductOperatingPage.module.scss';

const INITIAL_PRODUCT_OPERATION_FILTER: TypeProductOperatingFilter = {
  keyword: null,
  keywordValue: '',
  productRegisterStartDate: null,
  productRegisterEndDate: null,
  productUsageStatus: OPTION_ALL,
  mainCategoryCode: OPTION_ALL,
  middleCategoryCode: OPTION_ALL,
  thirdCategoryCode: OPTION_ALL,
  fourthCategoryCode: OPTION_ALL,
  currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
  pageSize: INITIAL_PRODUCT_PAGE_SIZE,
};

const ProductOperatingPage = () => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const [
    productDetailModalValue,
    productModalRequiredValueStatus,
    setProductModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setProductModalSkuCodeStatus,
    resetProductModalSkuCodeStatus,
    focusFieldRefs,
    resetFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productDetailModalValue,
      state.productModalRequiredValueStatus,
      state.setProductModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setProductModalSkuCodeStatus,
      state.resetProductModalSkuCodeStatus,
      state.focusFieldRefs,
      state.resetFocusFieldRefs,
    ])
  );

  const queryClient = useQueryClient();

  const codesMenuQuery = useGetCodesMenuMenuCodeQuery<TypeProductOperatingPageCodesMenu>({ menuCode: 'PROD_SKU' });
  const mainCategoryQuery = useGetCategoriesTypesTypeQuery({ type: 'MAIN', silent: true, dimmed: false });
  const orderTypeInfoQuery = useGetOrderTypeInfoQuery({ silent: true });
  const updateSkusMutation = usePutThirdPartiesSkusMutation();
  const excelDownloadMutation = usePostThirdPartySkuExcelDownloadMutation();

  const optionProductKeyword = useMemo(
    () => (codesMenuQuery.data ? transformToSelectOptions(codesMenuQuery.data.skuKeyword?.codeItemList) : []),
    [codesMenuQuery.data]
  );
  const optionProductUsageStatus = useMemo<TypeSelectOption<string>[]>(() => {
    if (!codesMenuQuery.data) return [];
    return hasPermissionFBK
      ? [OPTION_ALL, ...transformToSelectOptions(codesMenuQuery.data.productUsageStatus.codeItemList)]
      : [OPTION_ALL, ...Object.values(PRODUCT_USAGE_STATUS)];
  }, [codesMenuQuery.data]);

  const [listQueryParams, setListQueryParams] = useState<TypeGetSkusParams | null>(null);
  const skusQuery = useGetSkusQuery({
    params: listQueryParams ?? ({} as TypeGetSkusParams),
    enabled: !!listQueryParams,
  });

  const [productOperatingFilter, setProductOperatingFilter] = useState<TypeProductOperatingFilter>(() => ({
    ...INITIAL_PRODUCT_OPERATION_FILTER,
    keyword: optionProductKeyword[0] ?? null,
  }));

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: INITIAL_PRODUCT_OPERATION_FILTER,
  });

  const pageOptions = Object.values(PRODUCT_PAGE_SIZE);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const handleSearch = useCallback(
    (filter: TypeProductOperatingFilter) => {
      setProductOperatingFilter({ ...filter, keywordValue: filter.keywordValue?.trim() });

      const params: TypeGetSkusParams = {
        status: 'ACTIVE',
        isActive: true,
        keyWord: String(filter.keyword?.value ?? ''),
        keyWordValue: filter.keywordValue?.trim(),
        fromDate: formatDate(filter.productRegisterStartDate, 'YYYY.MM.DD'),
        toDate: formatDate(filter.productRegisterEndDate, 'YYYY.MM.DD'),
        useMethodCode: filter.productUsageStatus.value,
        mainCategoryCode: String(filter.mainCategoryCode.value),
        middleCategoryCode: String(filter.middleCategoryCode.value),
        thirdCategoryCode: String(filter.thirdCategoryCode.value),
        fourthCategoryCode: String(filter.fourthCategoryCode.value),
        limit: filter.pageSize,
        cursor: filter.currentPage,
      };
      queryClient.invalidateQueries({ queryKey: productKeys.skus(params) });
      setListQueryParams(params);
    },
    [queryClient]
  );

  const [selectedSkuCode, setSelectedSkuCode] = useState<string | null>(null);

  const productDetailModal = useModal({
    onOpen: () => {
      resetProductModalRequiredValueStatus();
      setProductModalSkuCodeStatus({
        isValid: true,
        validationMessage: '',
        status: 'default',
      });
    },
    onClose: () => resetFocusFieldRefs(),
    onCloseCompleted: () => setSelectedSkuCode(null),
    onConfirm: () => handleSearch(productOperatingFilter),
  });

  const { columnDefs } = getProductOperatingColumnDefs({
    onClickViewDetail: (skuCode) => handleOpenDetailModal(skuCode),
  });

  useEffect(() => {
    if (!codesMenuQuery.data) return;

    setProductOperatingFilter((prev) => ({ ...prev, keyword: prev.keyword ?? optionProductKeyword?.[0] ?? null }));
  }, [codesMenuQuery.data]);

  useEffect(() => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      middleCategoryCode: OPTION_ALL,
      thirdCategoryCode: OPTION_ALL,
      fourthCategoryCode: OPTION_ALL,
    });
  }, [productOperatingFilter.mainCategoryCode]);

  useEffect(() => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      thirdCategoryCode: OPTION_ALL,
      fourthCategoryCode: OPTION_ALL,
    });
  }, [productOperatingFilter.middleCategoryCode]);

  useEffect(() => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      fourthCategoryCode: OPTION_ALL,
    });
  }, [productOperatingFilter.thirdCategoryCode]);

  useEffect(() => {
    checkFilterChange(productOperatingFilter);
  }, [productOperatingFilter]);

  const hasFilterError = codesMenuQuery.isError || orderTypeInfoQuery.isError || mainCategoryQuery.isError;

  useEffect(() => {
    if (hasFilterError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [hasFilterError]);

  const mainCategoryOptions = mainCategoryQuery.data ? transformToSelectOptions(mainCategoryQuery.data) : [];

  // 중분류 카테고리 조회 (대분류 선택 시)
  const selectedMainCode = productOperatingFilter.mainCategoryCode.value;
  const middleCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMainCode !== OPTION_ALL.value ? selectedMainCode : null,
    dimmed: false,
  });

  // 소분류 카테고리 조회 (중분류 선택 시)
  const selectedMiddleCode = productOperatingFilter.middleCategoryCode.value;
  const thirdCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMiddleCode !== OPTION_ALL.value ? selectedMiddleCode : null,
    dimmed: false,
  });

  // 세분류 카테고리 조회 (소분류 선택 시)
  const selectedThirdCode = productOperatingFilter.thirdCategoryCode.value;
  const fourthCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedThirdCode !== OPTION_ALL.value ? selectedThirdCode : null,
    dimmed: false,
  });

  const optionMiddleCategory = useMemo(() => {
    if (!middleCategoryQuery.data) return [];
    return [OPTION_ALL, ...transformToSelectOptions(middleCategoryQuery.data)];
  }, [middleCategoryQuery.data]);

  const optionThirdCategory = useMemo(() => {
    if (!thirdCategoryQuery.data) return [];
    return [OPTION_ALL, ...transformToSelectOptions(thirdCategoryQuery.data)];
  }, [thirdCategoryQuery.data]);

  const optionFourthCategory = useMemo(() => {
    if (!fourthCategoryQuery.data) return [];
    return [OPTION_ALL, ...transformToSelectOptions(fourthCategoryQuery.data)];
  }, [fourthCategoryQuery.data]);

  useEffect(() => {
    if (!skusQuery.data) return;
    markDownloadReady({ filterValue: productOperatingFilter, hasData: skusQuery.data.data.length > 0 });
  }, [skusQuery.data]);

  const handleChangeKeyword = (_: unknown, selectedOption: TypeSelectOption) => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      keyword: selectedOption,
      keywordValue: '',
    });
  };

  const handleChangeKeywordValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setProductOperatingFilter({
      ...productOperatingFilter,
      keywordValue: value,
    });
  };

  const handleChangeRegisterDate = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start;
    const endDate = selectedDate?.end;

    setProductOperatingFilter({
      ...productOperatingFilter,
      productRegisterStartDate: startDate,
      productRegisterEndDate: endDate,
    });
  };

  const handleChangeProductUsageStatus = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      productUsageStatus: selectedOption,
    });
  };

  const handleChangeMainCategory = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      mainCategoryCode: selectedOption,
    });
  };

  const handleChangeMiddleCategory = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      middleCategoryCode: selectedOption,
    });
  };

  const handleChangeThirdCategory = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      thirdCategoryCode: selectedOption,
    });
  };

  const handleChangeFourthCategory = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setProductOperatingFilter({
      ...productOperatingFilter,
      fourthCategoryCode: selectedOption,
    });
  };

  const handleChangeLimit = (_: unknown, { value }: TypeSelectOption<TypeProductPageSize>) => {
    handleSearch({
      ...productOperatingFilter,
      pageSize: value,
      currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
    });
  };

  const handleClickPage = (e: MouseEvent<HTMLButtonElement>) => {
    const newPage = Number(e.currentTarget.value);

    handleSearch({
      ...productOperatingFilter,
      currentPage: newPage - 1,
    });
  };

  const handleSubmitSearch = (e: FormEvent) => {
    e.preventDefault();

    handleSearch({
      ...productOperatingFilter,
      currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
    });
  };

  const handleClickReset = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    setProductOperatingFilter({
      keyword: optionProductKeyword[0] ?? null,
      keywordValue: '',
      productRegisterStartDate: null,
      productRegisterEndDate: null,
      productUsageStatus: OPTION_ALL,
      mainCategoryCode: OPTION_ALL,
      middleCategoryCode: OPTION_ALL,
      thirdCategoryCode: OPTION_ALL,
      fourthCategoryCode: OPTION_ALL,
      currentPage: INITIAL_PRODUCT_PAGE_OFFSET,
      pageSize: INITIAL_PRODUCT_PAGE_SIZE,
    });
  };

  const handleExcelDownload = () => {
    excelDownloadMutation.mutate(
      {
        keyWord: String(searchedFilter.keyword?.value ?? ''),
        keyWordValue: searchedFilter.keywordValue,
        status: 'ACTIVE',
        isActive: true,
        mainCategoryCode: String(searchedFilter.mainCategoryCode.value),
        middleCategoryCode: String(searchedFilter.middleCategoryCode.value),
        thirdCategoryCode: String(searchedFilter.thirdCategoryCode.value),
        fourthCategoryCode: String(searchedFilter.fourthCategoryCode.value),
        fromDate: formatDate(searchedFilter.productRegisterStartDate, 'YYYY.MM.DD'),
        toDate: formatDate(searchedFilter.productRegisterEndDate, 'YYYY.MM.DD'),
      },
      {
        onSuccess: () => {
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const validateInputValues = (
    inputValues: TypeProductModalInput
  ): {
    updatedState: Record<string, TypeInputState>;
    isValid: boolean;
  } => {
    const initialStatus = Object.keys(productModalRequiredValueStatus).reduce(
      (acc, key) => ({ ...acc, [key]: DEFAULT_INPUT_STATUS }),
      {}
    );

    const updatedState = validateProductFields(inputValues, hasPermissionFBK ? 'FBK' : '3PL', initialStatus);

    const hasErrors = Object.values(updatedState).some((status) => status.status === 'error');

    return {
      updatedState,
      isValid: !hasErrors,
    };
  };

  const handleConfirmDetailModal = () => {
    const { isValid, updatedState } = validateInputValues(productDetailModalValue);

    setProductModalRequiredValueStatus({
      ...productModalRequiredValueStatus,
      ...updatedState,
    });

    if (isValid) {
      updateSkusMutation.mutate(
        {
          ...productDetailModalValue,
          supplierCodes: productDetailModalValue.supplierCodes?.map(({ value }) => value) ?? [],
          shelfLifeType: productDetailModalValue.shelfLifeType?.value ?? '',
          temperatureType: productDetailModalValue.temperatureType?.value ?? '',
          mainCategoryCode: productDetailModalValue.mainCategoryCode?.value ?? '',
          middleCategoryCode: productDetailModalValue.middleCategoryCode?.value ?? '',
          thirdCategoryCode: productDetailModalValue.thirdCategoryCode?.value ?? '',
          fourthCategoryCode: productDetailModalValue.fourthCategoryCode?.value ?? '',
          unit: productDetailModalValue.unit?.value ?? '',
          productType: productDetailModalValue.productType?.value ?? '',
          kurlyOrderType: productDetailModalValue.kurlyOrderType?.value ?? '',
          parcelOrderType: productDetailModalValue.parcelOrderType?.value ?? '',
          massDocumentNum: productDetailModalValue.massDocumentNum?.value ?? '',
          kurlyKeepType: productDetailModalValue.kurlyKeepType?.value ?? '',
          deliveryDocumentNum: productDetailModalValue.deliveryDocumentNum?.value ?? '',
          packingDirection: productDetailModalValue.packingDirection?.value ?? '',
          sellingType: productDetailModalValue.sellingType,
          stockSafeDate: productDetailModalValue.stockSafeDate ? Number(productDetailModalValue.stockSafeDate) : null,
          useMethodCode: productDetailModalValue.useMethodCode,
        },
        {
          onSuccess: () => {
            resetProductModalRequiredValueStatus();
            resetProductModalSkuCodeStatus();

            toastService.addToast({
              message: (
                <span>
                  상품 정보가 등록 요청되었습니다.
                  <br />
                  <b>상품 등록/수정</b> 메뉴에서 처리 상태 확인이 가능합니다.
                </span>
              ),
            });

            productDetailModal.handleConfirmModal();
            resetFocusFieldRefs();
          },
        }
      );
    } else {
      focusFirstErrorField(focusFieldRefs, updatedState);
    }
  };

  const handleOpenDetailModal = (skuCode: string) => {
    setSelectedSkuCode(skuCode);
    productDetailModal.handleOpenModal();
  };

  return (
    <PageWrapper>
      <PageTitle
        title="운영 상품 조회"
        breadcrumbs={BREAD_CRUMB[PATH.PRODUCT_OPERATING]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSubmitSearch}>
          <div className={styles['filters-container']}>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                키워드
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  placeholder="선택"
                  value={productOperatingFilter.keyword}
                  options={optionProductKeyword}
                  onChange={handleChangeKeyword}
                  size="small"
                />
                <Input
                  type="text"
                  value={productOperatingFilter.keywordValue}
                  placeholder="입력"
                  size="small"
                  maxLength={200}
                  onChange={handleChangeKeywordValue}
                />
              </div>
            </div>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                상품 등록일
              </Text>
              <DateRangePicker
                allowSingleDateSelect
                size="small"
                placeholder={{
                  start: '시작일',
                  end: '종료일',
                }}
                value={getDateRangeInfo(
                  productOperatingFilter?.productRegisterStartDate,
                  productOperatingFilter?.productRegisterEndDate
                )}
                onChange={handleChangeRegisterDate}
              />
            </div>
            <div className={styles['filter-column-container']}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  상품 사용 여부
                </Text>
                <Select
                  placeholder="선택"
                  value={productOperatingFilter.productUsageStatus}
                  options={optionProductUsageStatus}
                  size="small"
                  onChange={handleChangeProductUsageStatus}
                />
              </div>
            </div>
            <div className={styles['filter-column-container']}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  대분류
                </Text>
                <Select
                  placeholder="선택"
                  value={productOperatingFilter.mainCategoryCode}
                  options={[OPTION_ALL, ...mainCategoryOptions]}
                  size="small"
                  onChange={handleChangeMainCategory}
                />
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  중분류
                </Text>
                <Select
                  disabled={!optionMiddleCategory.length}
                  placeholder="선택"
                  value={productOperatingFilter.middleCategoryCode}
                  options={optionMiddleCategory}
                  size="small"
                  onChange={handleChangeMiddleCategory}
                />
              </div>
            </div>
            <div className={styles['filter-column-container']}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  소분류
                </Text>
                <Select
                  disabled={!optionThirdCategory.length}
                  placeholder="선택"
                  value={productOperatingFilter.thirdCategoryCode}
                  options={optionThirdCategory}
                  size="small"
                  onChange={handleChangeThirdCategory}
                />
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  세분류
                </Text>
                <Select
                  disabled={!optionFourthCategory.length}
                  placeholder="선택"
                  value={productOperatingFilter.fourthCategoryCode}
                  options={optionFourthCategory}
                  size="small"
                  onChange={handleChangeFourthCategory}
                />
              </div>
            </div>
          </div>
          <div className={styles['filter-actions']}>
            <div className={styles['filter-actions-left-field']} />
            <div className={styles['filter-actions-center-field']}>
              <Button type="submit" size="small">
                검색
              </Button>
            </div>
            <div className={styles['filter-actions-right-field']}>
              <TextButton
                className={styles['filter-reset-button']}
                leftIcon={<RefreshIcon size={16} />}
                size="small"
                color="secondary"
                onClick={handleClickReset}
              >
                필터 초기화
              </TextButton>
            </div>
          </div>
        </Form>
      )}

      <div className={styles['view-section']}>
        <TableTopField
          dataTotalCount={skusQuery.data?.pagination.listTotal ?? 0}
          rightButton={
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              leftIcon={<ExcelIcon size={16} />}
              disabled={!isDownloadReady}
              onClick={handleExcelDownload}
            >
              엑셀 다운로드
            </Button>
          }
        />
        <div className={styles['view-section-table-wrapper']}>
          <Table
            className={styles['view-table']}
            data={skusQuery.data?.data ?? null}
            options={{ columnDefs }}
            rowStatusType={!listQueryParams ? 'INIT' : 'EMPTY'}
          />
        </div>
      </div>
      <div className={styles['view-actions-section']}>
        <div className={styles['view-options']}>
          <Select
            className={styles['limit-select']}
            options={pageOptions}
            value={PRODUCT_PAGE_SIZE[productOperatingFilter.pageSize]}
            size="tiny"
            dropdownPlacement="top-left"
            onChange={handleChangeLimit}
          />
        </div>
        {skusQuery.data?.pagination.listTotal ? (
          <Pagination
            className={styles['pagination']}
            currentPage={productOperatingFilter.currentPage + 1}
            totalCount={skusQuery.data.pagination.listTotal}
            pageSize={productOperatingFilter.pageSize}
            onClick={handleClickPage}
          />
        ) : null}
      </div>
      <ProductMenuCodeProvider value="PROD_SKU">
        {productDetailModal.isOpen && selectedSkuCode && (
          <ProductDetailsModal
            skuCode={selectedSkuCode}
            isActiveSku
            open={productDetailModal.isOpen}
            onCancel={productDetailModal.handleCloseModal}
            onConfirm={handleConfirmDetailModal}
          />
        )}
      </ProductMenuCodeProvider>
    </PageWrapper>
  );
};

export default ProductOperatingPage;
